import { useState } from 'react';
import { HoldingsCompany, HOLDINGS_COMPANY_STATUSES } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface CompanyFormProps {
  company?: HoldingsCompany | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function CompanyForm({ company, onClose, onSaved }: CompanyFormProps) {
  const isEditing = !!company;

  const [name, setName] = useState(company?.name || '');
  const [status, setStatus] = useState(company?.status || 'active');
  const [ownershipStakePercentage, setOwnershipStakePercentage] = useState(
    company?.ownershipStakePercentage?.toString() || ''
  );
  const [description, setDescription] = useState(company?.description || '');
  const [website, setWebsite] = useState(company?.website || '');
  const [industry, setIndustry] = useState(company?.industry || '');
  const [foundedDate, setFoundedDate] = useState(company?.foundedDate?.split('T')[0] || '');
  const [acquisitionDate, setAcquisitionDate] = useState(company?.acquisitionDate?.split('T')[0] || '');
  const [exitDate, setExitDate] = useState(company?.exitDate?.split('T')[0] || '');
  const [notes, setNotes] = useState(company?.notes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Company name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        status,
        ownershipStakePercentage: ownershipStakePercentage ? parseFloat(ownershipStakePercentage) : null,
        description: description.trim() || null,
        website: website.trim() || null,
        industry: industry.trim() || null,
        foundedDate: foundedDate || null,
        acquisitionDate: acquisitionDate || null,
        exitDate: status === 'sold' ? exitDate || null : null,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/holdings/companies/${company.id}`
        : `${API_URL}/holdings/companies`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save company');
      }
    } catch (e) {
      setError('Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Company' : 'Add Company'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Company Name & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Company Name *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Corp"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
                {HOLDINGS_COMPANY_STATUSES.map(s => (
                  <option key={s.status} value={s.status}>{s.label}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Ownership & Industry */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Ownership Stake
              </label>
              <div className="relative">
                <Input
                  value={ownershipStakePercentage}
                  onChange={(e) => setOwnershipStakePercentage(e.target.value)}
                  placeholder="25"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Industry
              </label>
              <Input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Technology"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Website
            </label>
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
              type="url"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the company..."
              rows={2}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Founded Date
              </label>
              <Input
                type="date"
                value={foundedDate}
                onChange={(e) => setFoundedDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Acquisition Date
              </label>
              <Input
                type="date"
                value={acquisitionDate}
                onChange={(e) => setAcquisitionDate(e.target.value)}
              />
            </div>
            {status === 'sold' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Exit Date
                </label>
                <Input
                  type="date"
                  value={exitDate}
                  onChange={(e) => setExitDate(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this company..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Company'} />
          </div>
        </form>
      </div>
    </div>
  );
}
